import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Location, CurrencyPipe } from '@angular/common';
import { Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { FormArray, FormGroup, AbstractControl, FormControl } from '@angular/forms';

import * as dashboardActions from '../../../pillar/dashboard/store/actions';
import * as actions from '../../store/actions';
import * as fromStore from '../../store/reducers';
import { ModuleConfiguration } from '../../datawarehouse.module.config';
import { DataWarehouseWorkspaceModelResponse } from '../../models/DataWarehouseWorkspaceModelResponse';
import { IListSelector } from '../../../pillar/workspace/models/IListSelector';
import { MetadataProvider } from '../../../pillar/workspace/services/MetadataProvider';
import { IMetadataProvider } from '../../../pillar/workspace/services/IMetadataProvider';
import { IMetadataEvent } from '../../../pillar/workspace/models/IMetadataEvent';
import { PageRequestModel } from '../../../core/models/PageRequestModel';
import { ModelBase } from '../../../pillar/common/models/ModelBase';
import { LoadEntityListPayload } from '../../models/LoadEntityListPayload';
import { IMetadataWorkspaceContext } from '../../../pillar/workspace/models/IMetadataWorkspaceContext';
import { MetadataWorkspaceComponent } from '../../../pillar/workspace/components/metadata-workspace/metadata-workspace.component';
import { IModelContext } from '../../../pillar/workspace/models/IModelContext';
import { Actions } from '@ngrx/effects';
import { RuleViolationModel } from '../../../common/models/RuleViolationModel';
import { LookupModel } from '../../../common/models/LookupModel';
import { AlertsComponent } from '../../../pillar/alerts/components/alerts.component';
import { RuleVioliationManagerService } from '../../../pillar/workspace/services/rule-violiation-manager.service';
import { DataWarehouseContextManagerService } from '../../service/data-warehouse-context-manager.service';
import { MatDialog } from '@angular/material';
import { AddAssociationModalComponent } from '../add-association-modal/add-association-modal.component';
import { IListPanelContext } from '../../../pillar/workspace/models/IListPanelContext';
import { RefreshListAction } from '../../../pillar/workspace/store/actions';
import { DataWarehouseService } from '../../../core/services';

@Component({
  selector: 'brd-data-warehouse-workspace',
  templateUrl: './data-warehouse-workspace.component.html',
  styleUrls: ['./data-warehouse-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RuleVioliationManagerService],
})
export class DataWarehouseWorkspaceComponent implements OnInit, OnDestroy {
  _key: string;
  _entityKey: string;
  _submittingEntityKey: string;
  _model: any;
  _metadata: any;
  _metadataProvider: IMetadataProvider;
  _subEntityScopes: string[] = [];
  _subEntityWorkspaceContexts: IMetadataWorkspaceContext[];
  _draftWorkspaceContexts: IMetadataWorkspaceContext[] = [];
  _metadataWorkspace: MetadataWorkspaceComponent;
  _rootContext: IMetadataWorkspaceContext;
  _initialized: boolean = false;
  _newAssociationKeys: string[] = [];
  _currentContext: any;
  _currentWorkspaceKey: string;

  loading$: Observable<boolean>;
  subEntityLoading$: Observable<boolean>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  editPermission: string;
  breadcrumbs: string[] = [];
  listSelectorFactory = (entityKey: string, scope: string) => this.createListSelectorFactory(entityKey, scope);
  isSubmitting: boolean = false;


  @ViewChild(MetadataWorkspaceComponent)
  set metadataWorkspace(metadataWorkspace: MetadataWorkspaceComponent) {
    this._metadataWorkspace = metadataWorkspace;
    if (this._metadataWorkspace) {
      this.ruleViolationManager.setRootForm(metadataWorkspace.workspaceForm);
    } else {
      this.ruleViolationManager.setRootForm(null);
    }
  }

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(
    private store: Store<any>,
    private router: Router,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private actions$: Actions,
    public ruleViolationManager: RuleVioliationManagerService,
    public dataWarehouseContextManager: DataWarehouseContextManagerService,
    private _dataWarehouseService: DataWarehouseService,
    public dialog: MatDialog,
    private location: Location,
  ) {
    this._metadataProvider = new MetadataProvider();
  }

  ngOnDestroy() {
    this.store.dispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse));
    this.destroyed$.next(true);
    this.dispose();
  }

  ngOnInit() {
    this.route.params.take(1).subscribe((p) => {
      this._key = p['key'];
      this._entityKey = p['entityKey'];
      const submittingEntityKey = p['submittingEntityKey'];
      this._submittingEntityKey = submittingEntityKey;

      this.dataWarehouseContextManager.trySelectByKey(submittingEntityKey);

      this.getSelection();

      let selectedSubscription = this.dataWarehouseContextManager.selectedSubmittingEntity$.subscribe((s) => {
        if (s) {
          this._initialized = true;
          this.getSelection();
          if (selectedSubscription) {
            selectedSubscription.unsubscribe();
            selectedSubscription = null;
          }
        }
      });

      this.loading$ = this.store.select(fromStore.getEditorStateLoading(this._entityKey, 'root'));

      this.store
        .select(fromStore.getEntitiesSelection(this._entityKey, 'root'))
        .takeUntil(this.destroyed$)
        .subscribe((m: DataWarehouseWorkspaceModelResponse) => {
          if (m) {
            this._metadataProvider.merge(m.metaData);
            this.updateContext(m.model, this._metadataProvider.get(m.model.$type));
          } else {
            this._model = undefined;
            this._metadata = undefined;
          }
        });
    });

    this.store
      .select(fromStore.getListEditorsState)
      .takeUntil(this.destroyed$)
      .subscribe((editors) => {
        if (editors) {
          const workspaces = new Map<string, IMetadataWorkspaceContext>();
          for (const property in editors) {
            if (editors.hasOwnProperty(property) && this._subEntityScopes.indexOf(property) >= 0) {
              const responseModel: DataWarehouseWorkspaceModelResponse = editors[property].selected;
              if (responseModel) {
                const metadataProvider = new MetadataProvider();
                metadataProvider.merge(responseModel.metaData);
                const currentWorkspace = this._draftWorkspaceContexts.find((w) => w.model === responseModel.model);
                if (currentWorkspace) {
                  currentWorkspace.model = responseModel.model;
                  currentWorkspace.metadata = metadataProvider.get(responseModel.model.$type) || currentWorkspace.metadata;
                  currentWorkspace.scope = property.substr(property.indexOf('_') + 1);
                  workspaces.set(property, currentWorkspace);
                } else {
                  const workspace = {
                    model: responseModel.model,
                    metadata: metadataProvider.get(responseModel.model.$type),
                    metadataProvider: metadataProvider,
                    scope: property.substr(property.indexOf('_') + 1),
                    isClone: responseModel.isClone
                  };
                  workspaces.set(property, workspace);
                  this._draftWorkspaceContexts.push(workspace);
                }
              }
            }
          }
          this.refreshSubEntityWorkspaces(workspaces);
        } else {
          this._subEntityWorkspaceContexts = [];
        }
        this.changeDetectorRef.markForCheck();
      });

    this.actions$
      .ofType(actions.REVISE_ROOT_ENTITY_RESPONSE)
      .takeUntil(this.destroyed$)
      .subscribe((r: actions.ReviseRootEntityResponseAction) => {
        this.handleReviseResponse(r);
        this.isSubmitting = false;
        this.store.dispatch(new RefreshListAction());
        if(this._metadata.classMetadataItems.syncWithNextgate && r.payload.results.savedEntityKey){
          this.store.dispatch(
            new actions.SyncWithNextgateAction({
              key: r.payload.results.savedEntityKey,
              isNew: r.payload.key == 'new' ? true : false,
              currentAction: 'createUpdate'
            }),
          );
        }
      });
  }

  private getSelection() {
    if (this._initialized) {
      if (this._key === 'new') {
        this.store.dispatch(
          new actions.AddWorkspaceAction({
            entityKey: this._entityKey,
            scope: 'root',
            parentEntityKey: null,
            parentKey: null,
          }),
        );
      } else {
        this.store.dispatch(
          new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse, {
            entityKey: this._entityKey,
            scope: 'root',
            key: this._key,
          }),
        );
      }
    }
  }

  private fixUIIndex(array: FormArray) {
    for (const i in array.controls) {
      if (array.controls.hasOwnProperty(i)) {
        const control = array.controls[i];
        const uiIndexControl = control.get('UI_INDEX');
        uiIndexControl.patchValue(i);
      }
    }
  }

  private updateUIActions(name: string, form: FormGroup | FormArray) {
    Object.keys(form.controls)
      .reverse()
      .forEach((key) => {
        const currentControl = form.controls[key];

        if (key === 'UI_action') {
          if (currentControl.value === 'delete') {
            this.ruleViolationManager.unRegisterControls(form);
            if (form.parent instanceof FormArray) {
              const formArray = form.parent as FormArray;
              formArray.removeAt(name as any);
              this.fixUIIndex(formArray);
              this.tryRemoveItemContext(formArray, name as any);
            } else {
              (form.parent as FormGroup).removeControl(name);
            }
          } else if (currentControl.value === 'undo-delete') {
            currentControl.patchValue('create-update');
          }
        }
        if (currentControl.controls) {
          this.updateUIActions(key, currentControl);
        }
      });
  }

  private tryRemoveItemContext(formArray: FormArray, index: number): void {
    if (formArray.parent === this._metadataWorkspace._context.formGroup) {
      this._metadataWorkspace.panelContexts.forEach((p) => {
        if ((p as IListPanelContext).formArray === formArray) {
          (p as IListPanelContext).itemContexts.splice(index, 1);
        }
      });
    }
    if (this._metadataWorkspace._activeSubEntityWorkspace && formArray.parent === this._metadataWorkspace._activeSubEntityWorkspace.formGroup) {
      this._metadataWorkspace.subEntityWorkspace.panelContexts.forEach((p) => {
        if ((p as IListPanelContext).formArray === formArray) {
          (p as IListPanelContext).itemContexts.splice(index, 1);
        }
      });
    }
  }

  private handleReviseResponse(r: actions.ReviseRootEntityResponseAction): void {
    if (r.payload.results != null) {
      this.ruleViolationManager.clearAllRuleViolations();
      this._metadataWorkspace.workspaceForm.markAsUntouched();
      this.ruleViolationManager.ruleViolations = r.payload.results.ruleViolations;
      if (!r.payload.results.hasRuleViolation
        || r.payload.results.ruleViolations.every(rv => rv.violationType === 'Warning')) {
        this.updateUIActions(null, this._metadataWorkspace.workspaceForm);
        this._metadataWorkspace.workspaceForm.updateValueAndValidity({
          onlySelf: false,
          emitEvent: false,
        });
        const newEntityKeys = r.payload.results.newEntityKeys;
        for (const path in newEntityKeys) {
          if (newEntityKeys.hasOwnProperty(path)) {
            const workspace = this._draftWorkspaceContexts.find((w) => w.path === path);
            const control = this._metadataWorkspace.workspaceForm.get(path + '.key');
            if (control) {
              control.patchValue(newEntityKeys[path]);
            }
            if (workspace) {
              workspace.model.key = newEntityKeys[path];
            }
          }
        }
        const newModel = this._metadataWorkspace.workspaceForm.value;
        this._metadataWorkspace.workspaceForm.markAsPristine();
        newModel.key = r.payload.results.savedEntityKey;
        this._key = r.payload.results.savedEntityKey;
        if (this._key) {
          this._metadataWorkspace.workspaceForm.controls['key'].setValue(this._key);
          this.location.go(this.location.path(true).replace('new', this._key));
        }
        this.updateContext(newModel, this._metadata);
        this.alerts.addSuccess(this._metadata.classMetadataItems.displayName + ' Saved Successfully!');
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  private updateContext(model: any, metadata: any) {
    if(this._key == 'new'){
      this._model = { $type: model.$type };
    } else {
      this._model = model;
    }

    this._metadata = metadata;
    if (this._rootContext) {
      this._rootContext = {
        model: this._model,
        metadata: this._metadata,
        metadataProvider: this._metadataProvider,
        ...this._rootContext,
      };
    } else {
      this._rootContext = {
        model: this._model,
        metadata: this._metadata,
        metadataProvider: this._metadataProvider,
        scope: 'root',
      };
    }

    this.breadcrumbs = [this._metadata.classMetadataItems.displayName];
  }

  private getCurrentContext() {
    if(this._metadataWorkspace){
      return this._metadataWorkspace._activeSubEntityWorkspace;
    }
  }

  private refreshSubEntityWorkspaces(workspaces: Map<string, IMetadataWorkspaceContext>) {
    this._subEntityWorkspaceContexts = [];
    this.breadcrumbs = this._metadata ? [this._metadata.classMetadataItems.displayName] : [];
    workspaces.forEach((workspace: IMetadataWorkspaceContext, key: string) => {
      const index = this._subEntityScopes.indexOf(key);
      if (index >= 0) {
        this.breadcrumbs.splice(index + 1, 0, workspace.metadata.classMetadataItems.displayName);
        this._subEntityWorkspaceContexts.splice(index, 0, workspace);
      }
    });
    this._currentContext = this.getCurrentContext();
    this.subEntityLoading$ = undefined;
  }

  popWorkspace() {
    this._subEntityScopes = this._subEntityScopes.slice(0, this._subEntityScopes.length - 1);
    this.breadcrumbs = this.breadcrumbs.slice(0, this.breadcrumbs.length - 1);
    this._subEntityWorkspaceContexts = this._subEntityWorkspaceContexts.slice(0, this._subEntityWorkspaceContexts.length - 1);
  }

  createListSelectorFactory(entityKey: string, scope: string): IListSelector {
    return {
      loading: this.store.select(fromStore.getEditorStateLoading(entityKey, scope)),
      items: this.store.select(fromStore.getEntitiesList(entityKey, scope)),
      totalCount: this.store.select(fromStore.getEntitiesListTotalCount(entityKey, scope)),
    };
  }

  back() {
    if (this._subEntityScopes && this._subEntityScopes.length > 0) {
      this.popWorkspace();
    } else {
      this.router.navigateByUrl('/datawarehouse');
    }
  }

  submit() {
    this._metadataWorkspace.workspaceForm.markAsUntouched();
    this.isSubmitting = true;
    this.store.dispatch(
      new actions.ReviseRootEntityAction({
        entityKey: this._entityKey,
        key: this._key,
        scope: 'root',
        model: this.getDirtyFormValues(this._metadataWorkspace.workspaceForm),
      }),
    );
  }

  onSubEntityPageRequest(request: IMetadataEvent<PageRequestModel>) {
    const payload = new LoadEntityListPayload();
    payload.page = request.data.page;
    payload.pageSize = request.data.pageSize;
    payload.sort = request.data.sort;
    payload.sortDirection = request.data.sortDirection;
    payload.searchTerm = request.data.searchTerm;
    payload.entityKey = request.metadata.entityKey;
    payload.scope = request.scope;
    payload.parentEntityKey = request.parentEntityKey;
    payload.parentKey = request.parentKey;
    payload.showDeleted = request.showDeleted;
    this.store.dispatch(new dashboardActions.QueryListAction(ModuleConfiguration.moduleName, ModelBase, payload));
  }

  onSubEntitySelected(event: IMetadataEvent<any>) {
    this._currentWorkspaceKey = event.data.key;
    let entityKey = event.metadata.entityKey;
    if (!entityKey) {
      const metadata = this._metadataProvider.get(event.metadata.$type);
      entityKey = metadata.classMetadataItems.entityKey;
    }
    this._subEntityScopes.push(`${entityKey}_${event.scope}`);
    
    var itemPath = event.scope + '.' + event.data.UI_INDEX;

    const currentWorkspace = this._draftWorkspaceContexts.find((w) => w.path === itemPath);

    if (currentWorkspace) {
      this.store.dispatch(
        new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse, {
          entityKey: entityKey,
          scope: event.scope,
          model: { model: currentWorkspace.model },
        }),
      );
    } else {
      this.store.dispatch(
        new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse, {
          entityKey: entityKey,
          scope: event.scope,
          key: event.data.key,
        }),
      );
      this.subEntityLoading$ = this.store.select(fromStore.getEditorStateLoading(entityKey, event.scope));
    }
  }

  onAssociationAdded(event: IMetadataEvent<any>) {
    let entityKey = event.metadata.entityKey;
    if (!entityKey) {
      const metadata = this._metadataProvider.get(event.metadata.$type);
      entityKey = metadata.classMetadataItems.entityKey;
    }

    this._dataWarehouseService.getWarehouseWorkspace(entityKey, event.data.key, this._submittingEntityKey)
      .subscribe((m: DataWarehouseWorkspaceModelResponse) => {
        if (m) {            
          const workspace : IMetadataWorkspaceContext = {
            model: m.model,
            metadata: this._metadataProvider.get(event.metadata.$type),
            metadataProvider: this._metadataProvider,
            scope: event.scope
          };
          var panelContexts = this._metadataWorkspace.panelContexts;
          this._metadataWorkspace.createAssociationPanelContexts(workspace);

          const workspaces = new Map<string, IMetadataWorkspaceContext>();
          workspaces.set(`${event.metadata.entityKey}_${event.scope}`, workspace);
          
          this._newAssociationKeys.push(event.scope);
          this._newAssociationKeys.push(workspace.model['key']);

          this._draftWorkspaceContexts.push(workspace);
          let contexts = this._subEntityWorkspaceContexts; 
          this._subEntityWorkspaceContexts = [workspace];
          this.changeDetectorRef.markForCheck();

          setTimeout(() => {
            this._subEntityWorkspaceContexts = contexts;
            this._metadataWorkspace.panelContexts = panelContexts;            
          },100);
        }
      });
  }

  onSubEntityCloned(event: IMetadataEvent<any>) {
    let entityKey = event.metadata.entityKey;
    if (!entityKey) {
      const metadata = this._metadataProvider.get(event.metadata.$type);
      entityKey = metadata.classMetadataItems.entityKey;
    }
    this._subEntityScopes.push(`${entityKey}_${event.scope}`);        

    this.store.dispatch(
      new actions.CloneWorkspaceAction({
        entityKey: entityKey,
        scope: event.scope,
        key: event.data.key,
      }),
    );
    this.subEntityLoading$ = this.store.select(fromStore.getEditorStateLoading(entityKey, event.scope));
  }

  onSubEntityAdded(event: IMetadataEvent<any>) {
    if (event.metadata.entityKey) {
      this._subEntityScopes.push(`${event.metadata.entityKey}_${event.scope}`);
      this.store.dispatch(
        new actions.AddWorkspaceAction({
          entityKey: event.metadata.entityKey,
          scope: event.scope,
          parentEntityKey: event.parentEntityKey,
          parentKey: event.parentKey,
        }),
      );
      this.subEntityLoading$ = this.store.select(fromStore.getEditorStateLoading(event.metadata.entityKey, event.scope));
    }    
  }

  onSubEntityAssociated(event: IMetadataEvent<any>){
    if (event.metadata.association) {
      const associationMetadata = this._metadataProvider.get(event.metadata.$type);
      const dialogRef = this.dialog.open(AddAssociationModalComponent, {
        width: '80vw',
        data: { metadata: associationMetadata, parent: { entityKey: this._entityKey, key: this._key} },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (result.associated) {              
            const associateEvent = {
              metadata: associationMetadata.classMetadataItems,
              scope: event.scope,
              data: result.associated,
              parentEntityKey: event.parentEntityKey,
              parentKey: event.parentKey,
            };
            this.subEntityLoading$ = Observable.of(true);
            this.changeDetectorRef.markForCheck();
            this.onAssociationAdded(associateEvent);
          } else if (result.selected) {
            const selectEvent = {
              metadata: associationMetadata.classMetadataItems,
              scope: event.scope,
              data: result.selected,
              parentEntityKey: event.parentEntityKey,
              parentKey: event.parentKey,
            };
            this._metadataWorkspace._activeSubEntityWorkspace.formGroup.markAsDirty();
            this._metadataWorkspace._activeSubEntityWorkspace.formGroup.controls['key'].markAsDirty();
            this._newAssociationKeys.push(event.scope);
            this._newAssociationKeys.push(selectEvent.data.key);
            this.onSubEntitySelected(selectEvent);
          }
          else if (result.add) {
            const addEvent = {
              metadata: associationMetadata.classMetadataItems,
              scope: event.scope,
              parentEntityKey: event.parentEntityKey,
              parentKey: event.parentKey,
              data: null,
            };
            this.onSubEntityAdded(addEvent);
          }
        }
      });
    }
  }

  getListMetadata(modelContext: IModelContext): any {
    return this._metadataProvider.get(modelContext.metadata.$type);
  }

  private getDirtyFormValues(form: any): any {
    const dirtyValues = form instanceof FormArray ? [] : {};

    Object.keys(form.controls).forEach((key) => {
      const currentControl = form.controls[key];
      if (currentControl.dirty || key === 'key' || key === '$type' || key.indexOf('UI_') === 0 || 
        this._newAssociationKeys.indexOf(currentControl.value) > -1 || (this._newAssociationKeys.indexOf(key) > -1) ||
        (currentControl.nameIdentifier && this._newAssociationKeys.indexOf(currentControl.nameIdentifier) > -1))  {
        if (currentControl.controls) {
          dirtyValues[key] = this.getDirtyFormValues(currentControl);
        } else {
          dirtyValues[key] = currentControl.value;
        }
      }
    });

    return dirtyValues;
  }

  private cleanUpWorkspaces() {
    this._draftWorkspaceContexts.forEach((w) => {
      if (w.formGroup) {
        this.ruleViolationManager.removeFormGroup(w.formGroup);
        if(w.formGroup.parent){
          if ((w.formGroup.parent as FormGroup).removeControl) {
            (w.formGroup.parent as FormGroup).removeControl((w.formGroup as any).nameIdentifier);
          } else if ((w.formGroup.parent as FormArray).removeAt) {
            const index = (w.formGroup.parent as FormArray).controls.indexOf(w.formGroup);
            (w.formGroup.parent as FormArray).removeAt(index);
          }
        }
      }
    });
    this.breadcrumbs = this._metadata ? [this._metadata.classMetadataItems.displayName] : [];
    this._draftWorkspaceContexts = [];
    this._subEntityScopes = [];
    this._subEntityWorkspaceContexts = [];
  }

  private dispose() {
    this.cleanUpWorkspaces();
    if (this.metadataWorkspace) {
      this.ruleViolationManager.removeFormGroup(this.metadataWorkspace.workspaceForm);
    }
    this._model = null;
    this._metadata = null;
    this.metadataWorkspace = null;
    this.ruleViolationManager = null;
    this._metadataProvider = null;
    this._rootContext = null;
    this.breadcrumbs = null;
  }

  lookupCompare(lookup1: LookupModel, lookup2: LookupModel): boolean {
    if (!lookup1 && !lookup2) {
      return true;
    } else if (!lookup1 || !lookup2) {
      return false;
    }
    return lookup1.key === lookup2.key;
  }

  getParentAutoPopulateValue(context: any){
    if(!context.metadata.parentDataLinkOnOtherEntity)
    {
      return null;
    }

    if(context.parentDropdownContext && context.parentDropdownContext.formName
      && this._rootContext.formGroup.controls[context.parentDropdownContext.formName] && this._rootContext.formGroup.controls[context.parentDropdownContext.formName].value){
      return this._rootContext.formGroup.controls[context.parentDropdownContext.formName].value.key;
    }
    return null;
  }

  fieldHasWarningOnly(formGroup: any, modelContext : any) : boolean{
    if(!formGroup.controls[modelContext.property].errors){
      return false;
    }

    var ruleViolations = formGroup.controls[modelContext.property].errors["rule-violation"];
    if(!ruleViolations){
      return false;
    }

    if(Array.isArray(ruleViolations)){
      return ruleViolations.every(v => v.violationType == 'Warning');
    }
    else{
      if(ruleViolations["violationType"]){
        return ruleViolations["violationType"] == 'Warning';
      }
      return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/datawarehouse/components/data-warehouse-workspace/data-warehouse-workspace.component.ts